import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PromotionReportDialog from "../../components/PromotionReportDialog";
import { api } from "../../utils/helpers";
import { NO_TOPIC } from "../boardAdapter";

// Archivierte Paketstände (board.archive) erscheinen nicht im Report: jede Stufe, deren Stand der archivierte ist.
// Ein neuerer Stand desselben Pakets (etwa auf DEV) bleibt sichtbar, wie auf dem Board.
function archivedStagesOf(board, pkg) {
  const digests = new Set((board.archive || []).filter(entry => entry.packageId === pkg.packageId).map(entry => entry.scopeDigest).filter(Boolean));
  return new Set(Object.entries(pkg.cells || {}).filter(([, cell]) => cell?.scopeDigest && digests.has(cell.scopeDigest)).map(([key]) => key));
}
// Mehrzielmodus: Ziele einer Stufe mit Inhalt (Zielzelle mit Tree oder Commit); nur bei mehr als einem Ziel je Stufe.
const hasContent = cell => !!(cell?.tree || cell?.commit);
function targetsWithContent(board, pkg, stage) {
  const row = (board.stages || []).find(item => item.key === stage);
  if (!board.multiTarget || !(row?.targets?.length > 1)) return null;
  return row.targets.filter(target => hasContent(pkg.targets?.[stage]?.[target.targetId]));
}
// Archivierte Ziele: ein Eintrag mit targetId (sonst Standardziel) betrifft nur dieses Ziel seiner Stufe.
function archivedTargetKeys(board, pkg) {
  const keys = new Set();
  for (const entry of (board.archive || []).filter(item => item.packageId === pkg.packageId)) {
    for (const [stage, byTarget] of Object.entries(pkg.targets || {})) {
      for (const [id, cell] of Object.entries(byTarget)) {
        if (cell?.scopeDigest && cell.scopeDigest === entry.scopeDigest && id === (entry.targetId ?? "default")) keys.add(`${stage}@${id}`);
      }
    }
  }
  return keys;
}

// Paket mit Inhalt auf mindestens einer nicht archivierten Stufe (im Mehrzielmodus: einem nicht archivierten Ziel).
export function reportablePackage(board, pkg) {
  const archived = archivedStagesOf(board, pkg);
  const archivedTargets = board.multiTarget ? archivedTargetKeys(board, pkg) : null;
  return Object.entries(pkg.cells || {}).some(([key, cell]) => {
    const list = archivedTargets ? targetsWithContent(board, pkg, key) : null;
    if (list) return list.some(target => !archivedTargets.has(`${key}@${target.targetId}`)) || !!cell?.error;
    return (cell?.tree || cell?.commit || cell?.error) && !archived.has(key);
  });
}

// Der Report liest aktuelle Deploymentabsichten je Stufenbranch. Ein fehlender
// erwarteter Paketstand ist ein Lesefehler, keine leere Reportquelle.
export function stageReportPackages(board, pkg, data) {
  if (data?.packageId !== pkg.packageId || !Array.isArray(data?.stages)) throw new Error("STAGE_REPORT_INCOMPLETE");
  const configured = (board.stages || []).filter(stage => stage.configured).map(stage => stage.key);
  const expected = configured.filter(key => pkg.cells?.[key]?.tree || pkg.cells?.[key]?.commit || pkg.cells?.[key]?.error);
  for (const key of expected) {
    if (!data.stages.some(stage => stage.stage === key && Array.isArray(stage.artifacts))) throw new Error("STAGE_REPORT_INCOMPLETE");
  }
  const archived = archivedStagesOf(board, pkg);
  const archivedTargets = board.multiTarget ? archivedTargetKeys(board, pkg) : null;
  return data.stages.filter(stage => configured.includes(stage.stage) && (!archived.has(stage.stage) || targetsWithContent(board, pkg, stage.stage))).flatMap(stage => {
    if (!Array.isArray(stage.artifacts) || stage.artifacts.some(artifact => !artifact || typeof artifact.id !== "string" || !artifact.id.trim())) {
      throw new Error("STAGE_REPORT_INCOMPLETE");
    }
    const group = { packageId: pkg.packageId, packageName: data.packageName || pkg.packageName || pkg.packageId,
      sourceStage: stage.stage, plant: pkg.plant || null, artifacts: stage.artifacts, artifactIds: stage.artifacts.map(artifact => artifact.id),
      // Pakettransport (Welle 6): Kennzeichen aus dem Board bzw. den Paketdetails, sonst keine Anzeige.
      ...(pkg.transport === "package" || data.transport === "package" ? { transport: "package" } : {}) };
    // Der Inhalt einer Stufe ist für alle Ziele gleich; der Report führt ihn je Ziel mit Inhalt auf.
    const targets = archivedTargets ? targetsWithContent(board, pkg, stage.stage) : null;
    if (!targets) return [group];
    return targets.filter(target => !archivedTargets.has(`${stage.stage}@${target.targetId}`))
      .map(target => ({ ...group, targetId: target.targetId, targetName: target.instanceName }));
  });
}

/** Datenadapter für den bestehenden Reportdialog; nur lesende Paketdetailabrufe. */
export default function StageReportDialog({ board, spaceId, topicId, onClose }) {
  const { t } = useTranslation("freigabe");
  const topics = useMemo(() => [
    ...(board.packages.some(pkg => !pkg.topicId) ? [{ id: NO_TOPIC, name: t("stageBranches.topicFilter.none"), stagePackages: [] }] : []),
    ...(board.topics || []),
  ], [board, t]);
  const loadPackages = useCallback(async (sourceId, signal) => {
    // Vollständig archivierte Pakete werden gar nicht erst gelesen.
    const packages = board.packages.filter(pkg => (!sourceId || (sourceId === NO_TOPIC ? !pkg.topicId : pkg.topicId === sourceId))
      && reportablePackage(board, pkg));
    const results = new Array(packages.length);
    let next = 0, failed = false;
    // Höchstens zwei Paketabrufe gleichzeitig, unabhängig von der Topicgröße.
    async function worker() {
      while (!failed && !signal.aborted && next < packages.length) {
        const index = next++, pkg = packages[index];
        try {
          const data = await api(`/api/cicd/transport/stage-board/package?spaceId=${encodeURIComponent(spaceId)}&packageId=${encodeURIComponent(pkg.packageId)}`,
            { cache: "no-store", retry: false, signal });
          results[index] = stageReportPackages(board, pkg, data);
        } catch (error) { failed = true; throw error; }
      }
    }
    await Promise.all(Array.from({ length: Math.min(2, packages.length) }, worker));
    return results.flat();
  }, [board, spaceId]);
  return <PromotionReportDialog className="sb-dialog" topics={topics} defaultPackages={board.packages} stages={board.stages}
    initialTopicId={topicId || ""} defaultSourceLabel={t("stageBranches.topicFilter.all")} loadPackages={loadPackages} onClose={onClose}
    note={(board.archive || []).length ? t("report.archivedHidden") : null}/>;
}
